import { formatAddress, toTitleCase, unwrapConnection } from "@utils/utils";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Breadcrumb, BreadcrumbLink, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useGetClinicsQuery } from "@api/graphql/types-and-hooks";
import { AddEditClinicForm } from "@pages/Clinic/AddEditClinicForm";

export const EditClinic: React.FC = () => {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const numericId = clinicId ? atob(clinicId).split(":").pop() : undefined;

  const { data, isLoading } = useGetClinicsQuery(
    { clinicId: numericId },
    { queryKey: ["getClinic", clinicId, "edit"], enabled: !!clinicId },
  );

  const clinics = unwrapConnection(data?.clinics);
  const clinic = clinics?.[0];
  const listing = clinic?.cliniclistingSet?.edges?.[0]?.node;
  const clinicListingId = listing?.publicId;

  const address = clinic?.location
    ? formatAddress({
        addressLine1: toTitleCase(clinic.location.addressLine1) || undefined,
        addressLine2: toTitleCase(clinic.location.addressLine2) || undefined,
        city: toTitleCase(clinic.location.city),
        state: clinic.location.state,
        zip: clinic.location.postalCode,
      }).replace(/\n/g, " ")
    : "";

  const initialValues = clinic
    ? {
        name: clinic.name ?? "",
        address,
        phone: listing?.phoneNumber ?? "",
        website: listing?.websiteUrl ?? "",
        email: listing?.contactEmail ?? "",
        about: listing?.description ?? "",
        services: (listing?.servicesOffered as string[]) ?? [],
        modalities: (listing?.modalityTypes as string[]) ?? [],
        payors: (listing?.payors as string[]) ?? [],
      }
    : undefined;

  const breadcrumbLinks = [
    {
      type: "component",
      name: "Clinics",
      current: false,
      Component: (
        <Link to="/clinics" className="text-text-secondary">
          Clinics
        </Link>
      ),
    },
    {
      type: "component",
      name: clinic?.name || "...",
      current: false,
      Component: (
        <Link to={`/clinics/${clinicId}`} className="text-text-secondary">
          {clinic?.name || "..."}
        </Link>
      ),
    },
  ] as BreadcrumbLink[];

  if (isLoading) return null;

  return (
    <div className="flex flex-col items-center mt-8">
      <div className="flex flex-col gap-1 min-w-[70vw]">
        <Breadcrumb
          links={breadcrumbLinks}
          homeLink={{
            name: "Home",
            Component: (
              <Link className="text-text-secondary" to="/">
                Home
              </Link>
            ),
            current: false,
            type: "component",
          }}
        />

        <Headline>Edit Clinic</Headline>
        <Paragraph size="small" colorType="secondary">
          Update your clinic information.
        </Paragraph>

        <AddEditClinicForm
          setShowAddClinicForm={() => navigate(`/clinics/${clinicId}`)}
          initialValues={initialValues}
          clinicListingId={clinicListingId}
        />
      </div>
    </div>
  );
};
